import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lock, PlayCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import { AccessBadge, DemoTag, Reveal } from "@/components/ui-lux";
import { NotFound } from "@/pages/Misc";
import { LoadError } from "@/lib/useApi";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const load = () => { setC(undefined); api.get(`/content/courses/${slug}`).then((r) => setC(r.data)).catch((e) => setC(e.response?.status === 404 ? null : "error")); };
  useEffect(load, [slug, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useSEO(c && c !== "error" ? { title: c.seo_title || c.title, description: c.meta_description || c.description?.slice(0, 155), image: c.cover_image,
    jsonLd: { "@context": "https://schema.org", "@type": "Course", name: c.title, description: c.description, provider: { "@type": "Organization", name: "The Transformed Mind Life" } } } : {});
  if (c === null) return <NotFound />;
  if (c === "error") return <div className="pt-40 container-x"><LoadError onRetry={load} /></div>;
  if (!c) return <div className="min-h-screen" />;

  const act = async () => {
    if (!user) return nav(`/login?next=/courses/${slug}`);
    if (!c.locked) {
      setBusy(true);
      try { await api.post(`/me/courses/${c.id}/enroll`); nav(`/learn/${c.slug}`); } catch (e) { toast.error(errMsg(e)); }
      return setBusy(false);
    }
    if (c.access === "paid") {
      setBusy(true);
      try {
        const { data } = await api.post("/billing/checkout", { kind: "course", course_id: c.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } catch (e) { toast.error(errMsg(e)); setBusy(false); }
      return;
    }
    nav("/register");
  };
  const label = !user ? "Log in to enroll" : !c.locked ? "Start learning" : c.access === "paid" ? `Buy course — $${c.price}` : "Join free to enroll";
  const lessons = c.modules.reduce((n, m) => n + m.lessons.length, 0);

  return (
    <div data-testid="course-detail-page">
      <section className="pt-[116px] px-3 sm:px-6 lg:px-[8vw]">
        <div className="relative rounded-2xl overflow-hidden min-h-[520px] flex items-end">
          <img src={c.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C55] via-[#0A1C55]/70 to-black/20" />
          <Reveal className="relative p-6 sm:p-12 max-w-3xl">
            <div className="flex gap-2 mb-5"><AccessBadge access={c.access} /><DemoTag show={c.is_demo} /></div>
            <h1 className="h1" data-testid="course-title">{c.title}</h1>
            <p className="mt-5 text-slate-200 text-lg">{c.description?.split("\n")[0]}</p>
            <p className="mt-4 font-accent text-xs uppercase tracking-wider text-slate-300">{c.modules.length} modules · {lessons} lessons · {c.level}</p>
            <button onClick={act} disabled={busy} className="btn-gold mt-8" data-testid="course-primary-cta">{busy ? "Please wait..." : label}</button>
          </Reveal>
        </div>
      </section>
      <section className="container-x py-20 grid lg:grid-cols-[1.3fr_1fr] gap-14">
        <div>
          <span className="eyebrow">Curriculum</span>
          <div className="space-y-4">
            {c.modules.map((m, i) => (
              <div key={m.id} className="card-lux p-6" data-testid={`curriculum-module-${i}`}>
                <h3 className="font-serif text-xl font-semibold text-white">{m.title}</h3>
                <ul className="mt-4 space-y-3">
                  {m.lessons.map((l) => (
                    <li key={l.id} className="flex items-center justify-between text-sm text-slate-300">
                      <span className="flex items-center gap-3">{l.locked ? <Lock size={15} className="text-slate-500" /> : <PlayCircle size={15} className="text-[#D4AF37]" />}{l.title}</span>
                      <span className="text-xs text-slate-500">{l.free_preview && l.locked === false && c.locked ? "Preview" : l.duration}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div>
          <span className="eyebrow">About this course</span>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{c.description}</p>
          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {["Video lessons you can resume anytime", "Downloadable worksheets and PDFs", "Lesson quizzes and progress tracking"].map((t) => (
              <li key={t} className="flex gap-3"><CheckCircle2 size={16} className="text-[#D4AF37]" />{t}</li>
            ))}
          </ul>
          <Link to="/courses" className="btn-outline mt-10">All courses</Link>
        </div>
      </section>
    </div>
  );
}
